<template>
  <div class="category-filter">
    <div class="category-filter__header c-pointer" @click="toggleFilter()">
      <span class="category-filter__label">{{ label }}</span>

      <span v-show="!isEdit" class="category-filter__value">
        {{ categoryLabel }}
      </span>

      <b-img
        class="category-filter__chevron"
        :class="{ 'category-filter__chevron--open': isEdit }"
        :src="require('@/assets/icons/chevron-secondary.svg')"
      ></b-img>
    </div>

    <div v-show="isEdit" class="category-filter__body">
      <v-select
        v-if="searchable"
        v-model="selected"
        :options="searchableOptions"
        :reduce="(option) => option.value"
        :taggable="taggable"
        :create-option="(text) => ({ text, value: text })"
        :multiple="multiple"
        :close-on-select="!multiple"
        label="text"
        clearable
        :placeholder="`Search ${label}`"
        class="category-filter__select category-filter__select--searchable"
      />
      <b-form-select
        v-else
        v-model="selected"
        :options="options"
        size="sm"
        class="category-filter__select"
      />
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'CategoryFilter',

  components: {
    vSelect,
  },

  props: {
    reset: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      required: true,
    },
    // Header text for this filter (e.g. "Trade", "Location"). Defaults to
    // "Category" so any other pre-existing usage of this component keeps
    // its original label unless it opts in to something more specific.
    label: {
      type: String,
      default: 'Category',
    },
    // Opt-in typeahead search (vue-select) in place of the native
    // b-form-select — for long lists (e.g. Trade has 200+ entries) where
    // scrolling a native dropdown isn't usable. Off by default so every
    // other existing/future consumer keeps the plain select unchanged.
    searchable: {
      type: Boolean,
      default: false,
    },
    // Only meaningful alongside `searchable`. Lets typed text that doesn't
    // match any option still be selected as-is (vue-select's own taggable
    // mode) — for filters like Trade, where `categories` is a list of known
    // values but the underlying field is free text, so a value that's real
    // on someone's profile may not be in that list at all.
    taggable: {
      type: Boolean,
      default: false,
    },
    // Only meaningful alongside `searchable` — lets more than one option be
    // picked at once (vue-select's own multi-select/tags mode). `selected`
    // becomes an array instead of a single id, and `@changed` emits that
    // array rather than a scalar.
    multiple: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isEdit: false,
      selected: this.multiple ? [] : null,
    }
  },

  computed: {
    categoryLabel() {
      if (this.multiple) {
        if (!this.selected || !this.selected.length) return 'All'
        return this.selected.map((id) => this.labelFor(id)).join(', ')
      }
      if (this.selected) {
        return this.labelFor(this.selected)
      }
      return 'All'
    },

    options() {
      let options = this.categories.map((category) => {
        return {
          text: category.label,
          value: category.id,
        }
      })

      options.unshift({ text: `Select ${this.label}`, value: null })

      return options
    },

    searchableOptions() {
      return this.categories.map((category) => ({
        text: category.label,
        value: category.id,
      }))
    },
  },

  methods: {
    toggleFilter() {
      this.isEdit = !this.isEdit
    },

    labelFor(id) {
      const category = this.categories.find((category) => category.id === id)
      // A taggable value typed in directly (not one of the known
      // `categories`) has no matching entry to look up a label from — show
      // the value itself rather than silently dropping it from the label.
      return category ? category.label : id
    },
  },

  watch: {
    reset(val) {
      if (val) this.selected = this.multiple ? [] : null
    },

    // No per-filter "GO" button anymore — picking a value here just reports
    // it up to the parent's pending filter state and collapses the panel
    // (same close-on-pick feel GO used to give). It does NOT search by
    // itself; CandidateFilterSidebar only calls the API once, when its own
    // single "Apply Filters" button is clicked, so a run through several
    // filters in a row doesn't fire a search per pick. This also fires
    // (harmlessly, with the same null the field already holds) when `reset`
    // clears `selected` above — the parent's own onReset() already zeroes
    // its copy directly, so this is a no-op double-set in that case.
    //
    // deep: true — in multiple mode `selected` is an array; vue-select
    // mutates it via push/splice on each pick rather than reassigning it, so
    // a shallow watch would miss every change after the first.
    selected: {
      deep: true,
      handler() {
        // Collapsing on every pick in multiple mode would close the panel
        // after the first tag, defeating the point of picking several —
        // only auto-collapse for a single-value filter.
        if (!this.multiple) this.isEdit = false
        this.$emit('changed', {
          type: 'category_id',
          value: this.selected,
        })
      },
    },
  },
}
</script>

<style lang="scss" scoped>
// Self-contained styles: this component used to rely on an ancestor
// `.marketplace-wrapper` class (see src/assets/scss/pages/_marketplace.scss)
// for the chevron's cursor/alignment/rotation. That wrapper isn't used by
// any current page, so anywhere this component is mounted without it (e.g.
// CandidateFilterSidebar) rendered a plain, unrotated, misaligned icon.
.category-filter__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
}

.category-filter__label {
  font-weight: 700;
  flex-shrink: 0;
}

.category-filter__value {
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
  font-weight: 700;
  color: #6c7581;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-filter__chevron {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  transition: transform 0.15s ease;
}

.category-filter__chevron--open {
  transform: rotate(180deg);
}

.category-filter__body {
  padding: 0 1rem 1rem;
}

.category-filter__select {
  width: 100%;
  min-width: 0;
}

// vue-select ships its own box model, quite different from the app's
// globally-styled <select> (which isn't plain Bootstrap defaults — this app
// overrides .custom-select globally to 50px/2px border/8px radius, see
// [[global-css-gotchas]]). Themed via vue-select's own CSS custom properties
// rather than fighting its internal classes directly, so its built-in
// open/focus state rules (which already reference these same variables)
// keep working.
.category-filter__select--searchable {
  --vs-border-color: #e5e5e5;
  --vs-border-width: 2px;
  --vs-border-radius: 8px;
  // 0.875rem matches Bootstrap's `size="sm"` text size, which is what
  // Location/Availability/etc. actually render at via <b-form-select
  // size="sm">. v-select has no equivalent size prop, so without this it
  // fell back to the browser default (1rem) and read visibly larger/
  // different to the plain selects next to it.
  --vs-font-size: 0.875rem;
  --vs-search-input-color: #495057;
}

// min-height, not height: a fixed height held the box to a single line, so
// once a long value like "Apprentice Boilermaker/Welder/Fitter" gets
// selected and wraps inside the toggle (same "long trade name" problem as
// the dropdown list, just for the chosen value this time), the second line
// had nowhere to go but overflow past the box's own border. min-height keeps
// the box at 50px for the normal one-line case and lets it grow for a
// wrapped one instead of clipping/overflowing it.
.category-filter__select--searchable ::v-deep .vs__dropdown-toggle {
  min-height: 50px;
  height: auto;
  align-items: center;
  padding-left: 0.25rem;
  position: relative;
  // vue-select's own stylesheet clips this box's content to whatever
  // height it computes — with min-height as a floor, not a ceiling, a
  // 3-line value (e.g. "Apprentice Boilermaker/Welder/Fitter") still grew
  // the box, but the clip hid everything past the height vue-select itself
  // expected, chopping the last line mid-word ("...Fitte") with no visual
  // sign more text existed. overflow: visible lets the box's real height
  // (which min-height/height:auto already size correctly) actually show.
  overflow: visible;
}

// vue-select's own CSS sets .vs__selected-options to flex-basis: 100%, so
// simply letting the toggle row wrap (flex-wrap: wrap) demoted the
// clear/chevron icons to their own line unconditionally — even for a short
// value like "Welder" that fit fine before. Pinning .vs__actions to the
// corner instead keeps the normal single-line case looking exactly as it
// did (icons vertically centered next to the text), and for a wrapped value
// the reserved padding-right below keeps the text from ever running under
// the icons on any wrapped line.
//
// min-width: 0 overrides the flex default of min-width: auto, which floors
// a flex item at its content's min-content size — here that floor was the
// width of "Boilermaker/Welder/Fitter" (no space to break on), so the
// padding-right above was being ignored: the box just overflowed its
// allotted space instead of shrinking into it. Without this, the text can't
// actually wrap into the room the padding reserves.
.category-filter__select--searchable ::v-deep .vs__selected-options {
  box-sizing: border-box;
  padding-right: 3.5rem;
  min-width: 0;
  flex-wrap: wrap;
}

.category-filter__select--searchable ::v-deep .vs__actions {
  position: absolute;
  top: 8px;
  right: 6px;
}

// vue-select keeps the "Search Trade" text input mounted as its own flex
// item even once a value is already selected and the box is closed (it's
// only there so clicking the toggle again lets you type a replacement). With
// a 2-line wrapped value it can't fit next to the text anymore and drops to
// its own row — reserved-width-but-empty except for a blinking cursor, which
// read as a dead gap under the value. Hide it while closed; the box is still
// fully clickable to reopen the dropdown and search again, nothing here
// changes behaviour, just an empty row nobody was using.
.category-filter__select--searchable.v-select:not(.vs--open)
  ::v-deep
  .vs__selected
  ~ .vs__search {
  display: none;
}

.category-filter__select--searchable ::v-deep .vs__selected {
  color: #495057;
  // vue-select never applies --vs-font-size to the selected-value display,
  // only to the search-typing input — this is the property that actually
  // needs setting to match Location's size="sm" text.
  font-size: 0.875rem;
  white-space: normal;
  overflow-wrap: break-word;
  min-width: 0;
}

// vue-select hardcodes .vs__dropdown-menu to width:100% of the select —
// and the select itself is a narrow sidebar column, so "100%" still isn't
// much room. Most Trade values (e.g. "Apprentice Boilermaker/Welder/Fitter")
// don't fit that at all and either got clipped against the menu's edge, or
// wrapped so tight a word split mid-character. Let the menu grow to fit its
// content instead, capped so it can't itself overflow a narrow viewport.
.category-filter__select--searchable ::v-deep .vs__dropdown-menu {
  width: max-content;
  min-width: 100%;
  max-width: calc(100vw - 2rem);
}

// Belt-and-braces for the genuine outliers even the widened menu above can't
// fit on one line (e.g. "Boom Type Elevated Work Platform Operator > 11m -
// HI-Rail"): wrap rather than clip. overflow-wrap (not word-break: break-word,
// which Chromium computes as word-break: normal + overflow-wrap: anywhere —
// the "anywhere" part greedily fills each line to the max width and
// hyphenates mid-word) only breaks a word mid-character as a last resort,
// when a single unbroken word genuinely can't fit a line on its own.
.category-filter__select--searchable ::v-deep .vs__dropdown-option {
  white-space: normal;
  overflow-wrap: break-word;
}
</style>
